import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Play, Image as ImageIcon, Quote, ChevronDown } from "lucide-react";
import { Reveal, SectionHeading } from "../components/primitives";
import { PageHero, CTASection, StoryCard } from "../components/sections";
import axios from "axios";
import { GALLERY, VIDEOS } from "../data/site";
import { useSuccessStories } from "../lib/useSuccessStories";

const API = process.env.REACT_APP_BACKEND_URL;

const PAGE_SIZE = 12;

const Tabs = ({ tab, setTab }) => (
  <div className="flex flex-wrap gap-2" data-testid="stories-tabs">
    {[
      { id: "stories", label: "Success Stories" },
      { id: "photos", label: "Student Photos" },
      { id: "reviews", label: "Client Review Videos" },
      { id: "videos", label: "Customer Driving Videos" },
    ].map((t) => (
      <button
        key={t.id}
        onClick={() => setTab(t.id)}
        data-testid={`stories-tab-${t.id}`}
        className={`rounded-full px-6 py-3 text-sm font-extrabold uppercase tracking-wide transition-colors duration-200 ${
          tab === t.id ? "bg-dwc-green text-white" : "border border-dwc-green/25 bg-white text-dwc-green hover:bg-dwc-soft"
        }`}
      >
        {t.label}
      </button>
    ))}
  </div>
);

const Lightbox = ({ item, onClose }) => (
  <AnimatePresence>
    {item && (
      <motion.div
        className="fixed inset-0 z-[95] flex items-center justify-center bg-dwc-charcoal/90 p-4 backdrop-blur-sm"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        role="dialog"
        aria-modal="true"
        aria-label="Media viewer"
        data-testid="stories-lightbox"
      >
        <button onClick={onClose} data-testid="lightbox-close-btn" aria-label="Close viewer" className="absolute right-5 top-5 rounded-full bg-white/10 p-3 text-white transition-colors hover:bg-white/20">
          <X className="h-5 w-5" aria-hidden="true" />
        </button>
        <motion.div
          initial={{ scale: 0.94, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.94, opacity: 0 }}
          className="max-h-[85vh] w-full max-w-3xl"
          onClick={(e) => e.stopPropagation()}
        >
          {item.type === "video" ? (
            <video src={item.src} controls autoPlay playsInline className="max-h-[85vh] w-full rounded-2xl bg-black" data-testid="lightbox-video" />
          ) : (
            <div>
              <img src={item.src} alt={item.name ? `DWC student ${item.name}` : "DWC student with their car"} className="mx-auto max-h-[78vh] w-auto rounded-2xl object-contain" data-testid="lightbox-image" />
              {item.name && <p className="mt-3 text-center text-sm font-extrabold uppercase tracking-[0.2em] text-white">{item.name}</p>}
            </div>
          )}
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);

const LoadMore = ({ shown, total, onMore, testId }) =>
  shown < total ? (
    <div className="mt-12 text-center">
      <button
        onClick={onMore}
        data-testid={testId}
        className="inline-flex items-center gap-2 rounded-xl border-2 border-dwc-green bg-white px-7 py-4 text-sm font-bold uppercase tracking-wide text-dwc-green transition-colors hover:bg-dwc-soft"
      >
        Load More ({total - shown} remaining)
        <ChevronDown className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  ) : null;

const MediaEmptyState = ({ label }) => (
  <div className="rounded-2xl border-2 border-dashed border-dwc-green/25 bg-white p-12 text-center" data-testid="media-empty-state">
    <ImageIcon className="mx-auto h-8 w-8 text-dwc-green/50" aria-hidden="true" />
    <p className="mt-4 text-sm font-bold text-dwc-charcoal">{label}</p>
    <p className="mt-1 text-xs text-dwc-charcoal/55">The DWC team is uploading genuine student media — check back soon.</p>
  </div>
);

const PhotosGrid = ({ openItem, items = GALLERY }) => {
  const [shown, setShown] = useState(PAGE_SIZE);
  if (items.length === 0) return <MediaEmptyState label="Student photos are being updated." />;
  return (
    <>
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4" data-testid="photos-grid">
        {items.slice(0, shown).map((g, i) => (
          <Reveal key={g.thumb} delay={(i % 4) * 0.05}>
            <button
              onClick={() => openItem({ type: "photo", src: g.full, name: g.name })}
              data-testid={`photo-item-${i}`}
              aria-label={g.name ? `View photo of DWC student ${g.name}` : "View student photo"}
              className="group relative block w-full overflow-hidden rounded-2xl"
            >
              <img
                src={g.thumb}
                alt={g.name ? `DWC student ${g.name} with their car` : "Genuine DWC student with their car in Mumbai, Surat & Ahmedabad"}
                loading="lazy"
                className="aspect-[3/4] w-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <span className="absolute inset-0 bg-gradient-to-t from-dwc-charcoal/60 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" aria-hidden="true" />
              {g.name && (
                <span className="absolute bottom-3 left-3 rounded-full bg-dwc-green px-3 py-1 text-[10px] font-extrabold uppercase tracking-[0.15em] text-white">
                  {g.name}
                </span>
              )}
              <span className="absolute right-3 top-3 rounded-full bg-white/85 p-2 text-dwc-green opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                <ImageIcon className="h-4 w-4" aria-hidden="true" />
              </span>
            </button>
          </Reveal>
        ))}
      </div>
      <LoadMore shown={shown} total={items.length} onMore={() => setShown((s) => s + PAGE_SIZE)} testId="photos-load-more" />
    </>
  );
};

const VideosGrid = ({ openItem, items = VIDEOS }) => {
  const [shown, setShown] = useState(PAGE_SIZE);
  if (items.length === 0) return <MediaEmptyState label="Student video stories are being updated." />;
  return (
    <>
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4" data-testid="videos-grid">
        {items.slice(0, shown).map((v, i) => (
          <Reveal key={v} delay={(i % 4) * 0.05}>
            <button
              onClick={() => openItem({ type: "video", src: v })}
              data-testid={`video-item-${i}`}
              aria-label={`Play student video story ${i + 1}`}
              className="group relative block aspect-[3/4] w-full overflow-hidden rounded-2xl bg-dwc-charcoal"
            >
              <video src={`${v}#t=0.5`} preload="metadata" muted playsInline className="h-full w-full object-cover opacity-80 transition-opacity duration-300 group-hover:opacity-100" />
              <span className="absolute inset-0 flex items-center justify-center" aria-hidden="true">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-dwc-green text-white shadow-[0_10px_30px_rgba(23,107,58,0.5)] transition-transform duration-300 group-hover:scale-110">
                  <Play className="ml-0.5 h-6 w-6" />
                </span>
              </span>
              <span className="absolute bottom-3 left-3 rounded-full bg-white/85 px-3 py-1 text-[10px] font-extrabold uppercase tracking-[0.15em] text-dwc-green" aria-hidden="true">
                Student Video
              </span>
            </button>
          </Reveal>
        ))}
      </div>
      <LoadMore shown={shown} total={items.length} onMore={() => setShown((s) => s + PAGE_SIZE)} testId="videos-load-more" />
    </>
  );
};

const Stories = () => {
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get("tab") || "stories");
  const [item, setItem] = useState(null);
  const [media, setMedia] = useState([]);
  const successStories = useSuccessStories();
  useEffect(() => {
    axios.get(`${API}/api/store/stories`).then((r) => setMedia(r.data)).catch(() => {});
  }, []);
  const uploadedPhotos = media.filter((m) => m.media_type === "photo").map((m) => ({ thumb: m.media_url, full: m.media_url, name: m.name }));
  const reviewVideos = media.filter((m) => m.media_type === "review").map((m) => m.media_url);
  const drivingVideos = media.filter((m) => m.media_type === "video").map((m) => m.media_url);

  return (
    <>
      <PageHero
        eyebrow="Student Success Stories"
        lines={["Real Students.", "Real Cars.", "Real Confidence."]}
        text="See how learners from different backgrounds built their confidence with Drive With Confidence. Every photo, video and story here is a genuine DWC student — published on DWC's official channels."
      />
      <section className="bg-dwc-soft py-24 md:py-32" data-testid="stories-page-grid">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
            <SectionHeading
              eyebrow="Genuine Stories Only"
              number="01"
              title="From Driving Fear To Driving Confidence"
              text="Different ages, different fears, different starting points — one step-by-step system."
            />
          </div>
          <div className="mt-10">
            <Tabs tab={tab} setTab={setTab} />
          </div>
          <div className="mt-10">
            {tab === "stories" && (
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {successStories.length > 0 && <StoryCard story={successStories[0]} featured index={0} />}
                {successStories.slice(1).map((s, i) => (
                  <StoryCard key={s.name} story={s} index={i + 1} />
                ))}
              </div>
            )}
            {tab === "photos" && <PhotosGrid openItem={setItem} items={[...uploadedPhotos, ...GALLERY]} />}
            {tab === "reviews" && <VideosGrid openItem={setItem} items={reviewVideos} />}
            {tab === "videos" && <VideosGrid openItem={setItem} items={[...drivingVideos, ...VIDEOS]} />}
          </div>
          <Reveal delay={0.2}>
            <p className="mt-12 flex items-center justify-center gap-2 text-center text-xs font-semibold uppercase tracking-[0.2em] text-dwc-charcoal/40">
              <Quote className="h-3.5 w-3.5" aria-hidden="true" />
              All media sourced from DWC's official student success records
            </p>
          </Reveal>
        </div>
      </section>
      <Lightbox item={item} onClose={() => setItem(null)} />
      <CTASection title="Your Story Could Be Next." sub="Every confident DWC driver started exactly where you are now. Book an appointment to begin." chapter="02" />
    </>
  );
};

export default Stories;
